/**
 * Chat Room Type Definition
 * @typedef {Object} ChatRoom
 * @property {string} id - Unique identifier for the chat room
 * @property {string[]} participants - Array of user IDs in the chat
 * @property {Object} participantInfo - Information about participants
 * @property {string} participantInfo.[userId].name - Display name of participant
 * @property {string} participantInfo.[userId].email - Email of participant
 * @property {string} participantInfo.[userId].avatar - Avatar URL of participant (optional)
 * @property {firebase.firestore.Timestamp} createdAt - Chat creation timestamp
 * @property {firebase.firestore.Timestamp} updatedAt - Last activity timestamp
 * @property {Message} lastMessage - Last message in the chat
 * @property {number} unreadCount - Number of unread messages for each participant
 */

/**
 * Creates a new chat room object
 * @param {Object} params - Chat room parameters
 * @param {string[]} params.participants - Array of user IDs
 * @param {Object} params.participantInfo - Participant information
 * @returns {Omit<ChatRoom, 'id' | 'lastMessage' | 'unreadCount'>} - New chat room object
 */
export const createChatRoom = ({ participants, participantInfo }) => {
  if (participants.length !== 2) {
    throw new Error('Chat room must have exactly 2 participants');
  }

  return {
    participants: participants.sort(),
    participantInfo,
    createdAt: new Date(),
    updatedAt: new Date()
  };
};

/**
 * Validates a chat room object
 * @param {Object} chatRoom - Chat room object to validate
 * @returns {Object} - Validation result { isValid: boolean, errors: string[] }
 */
export const validateChatRoom = (chatRoom) => {
  const errors = [];

  if (!chatRoom.participants || chatRoom.participants.length !== 2) {
    errors.push('Chat room must have exactly 2 participants');
  }

  if (!chatRoom.participantInfo) {
    errors.push('Participant information is required');
  } else {
    chatRoom.participants.forEach(participantId => {
      if (!chatRoom.participantInfo[participantId]?.name) {
        errors.push(`Participant name is required for user: ${participantId}`);
      }
      if (!chatRoom.participantInfo[participantId]?.email) {
        errors.push(`Participant email is required for user: ${participantId}`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  createChatRoom,
  validateChatRoom
};